import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';

type InvitationRole = 'host' | 'guest';

interface InviteGuestDialogProps {
  open: boolean;
  onClose: () => void;
  houseId: string;
  houseName: string;
  onSuccess: () => void;
}

export const InviteGuestDialog: React.FC<InviteGuestDialogProps> = ({
  open,
  onClose,
  houseId,
  houseName,
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InvitationRole>('guest');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();

  const handleClose = () => {
    setEmail('');
    setRole('guest');
    setError(null);
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email) {
      setError(t('inviteGuest.emptyEmail'));
      return;
    }

    try {
      setLoading(true);
      await axios.post('/invitations', {
        email,
        houseId,
        role,
      });
      setSuccess(true);
      setEmail('');
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('inviteGuest.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('inviteGuest.title', { houseName })}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('inviteGuest.success')}
              </Alert>
            )}
            <TextField
              fullWidth
              label={t('inviteGuest.emailAddress')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || success}
              required
              helperText={t('inviteGuest.helper')}
              autoFocus
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{t('inviteGuest.accessRole')}</InputLabel>
              <Select
                value={role}
                label={t('inviteGuest.accessRole')}
                onChange={(event) => setRole(event.target.value as InvitationRole)}
                disabled={loading || success}
              >
                <MenuItem value="guest">{t('inviteGuest.roleGuest')}</MenuItem>
                <MenuItem value="host">{t('inviteGuest.roleHost')}</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mt: 2 }}>
              {role === 'host' ? t('inviteGuest.hostHelper') : t('inviteGuest.guestHelper')}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={loading || success}>
            {loading ? t('inviteGuest.sending') : t('inviteGuest.send')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InviteGuestDialog;
